import { Polygon } from "geojson";

export const getIdealZoomLevel = (polygon: Polygon): number => {
  const mapMaxZoom = 21;

  // Find the bounding box of the polygon.
  const bounds = polygon.coordinates[0].reduce(
    (acc, point) => ({
      // GeoJSON places longitude first, then latitude. Google Maps does it the other way around.
      minLat: Math.min(acc.minLat, point[1]),
      maxLat: Math.max(acc.maxLat, point[1]),
      minLng: Math.min(acc.minLng, point[0]),
      maxLng: Math.max(acc.maxLng, point[0]),
    }),
    {
      minLat: Number.POSITIVE_INFINITY,
      maxLat: Number.NEGATIVE_INFINITY,
      minLng: Number.POSITIVE_INFINITY,
      maxLng: Number.NEGATIVE_INFINITY,
    },
  );

  // Calculate the latitude and longitude range/span of the polygon.
  const latRange = bounds.maxLat - bounds.minLat;
  const lngRange = bounds.maxLng - bounds.minLng;

  // Calculate the portion of the viewport to fit the polygon.
  const viewportWidthPx = window.innerWidth / 3; // Map takes about a third of the screen width.
  const viewportHeightPx = window.innerHeight / 2; // Map about half the screen height.

  const calculateZoom = (span: number, viewportPx: number) => {
    /**
     * Calculates the appropriate zoom level so that a given geographic span/range
     * fits within a specified number of pixels in the viewport.
     */
    // Determines how many times we need to zoom in (base 2) so that the span fits into the viewport,
    // considering the world is 360 degrees wide and each tile is 256 pixels
    // (also see https://developers.google.com/maps/documentation/javascript/coordinates#world-coordinates).
    // Math.log2 is used because each zoom level doubles the scale.
    const mapTileSize = 256;
    const mapWorldWidth = 360; // The world is 360 degrees wide.
    return Math.floor(
      Math.log2((mapTileSize * mapWorldWidth) / (span * viewportPx)),
    );
  };

  // Calculate zoom levels for latitude and longitude.
  const latZoom = calculateZoom(latRange, viewportHeightPx);
  const lngZoom = calculateZoom(lngRange, viewportWidthPx);

  // Return the most appropriate zoom and make sure it is within the allowed range.
  return Math.min(Math.max(latZoom, lngZoom), mapMaxZoom);
};
